import { useEffect, useMemo, useState } from 'react'
import { FolderKanban, GitBranch, Plus, Search } from 'lucide-react'
import type { Kleur, Project, Repo } from '../../gedeeld/types'
import { api, github } from '../lib/api'
import { datum, geleden, KLEUREN, PROJECT_STATUS, vanInvoer } from '../lib/opmaak'
import { Link, navigeer, useRoute, useTitel, zetParam } from '../lib/router'
import { laadProjecten, meld, meldFout, projectBijwerken, useBasis, useStaat } from '../lib/staat'
import { Avatar, Badge, Kies, Knop, Laden, Leeg, Tabs, Veld, Venster, Voortgang } from './ui'

/* ===========================================================================
 *  Alle projecten
 * =========================================================================== */

type Filter = 'lopend' | 'alle' | 'archief'

export default function Projecten() {
  useTitel('Projecten', 'Alles waar aan gewerkt wordt')
  const { projecten, geladen, mensen } = useBasis()
  const { q } = useRoute()
  const [filter, setFilter] = useState<Filter>('lopend')
  const [zoek, setZoek] = useState('')
  const nieuw = q.get('nieuw') === '1'

  useEffect(() => { void laadProjecten() }, [])

  const zicht = useMemo(() => projecten
    .filter((p) => filter === 'archief' ? p.archief : filter === 'lopend' ? !p.archief && p.status !== 'afgerond' : !p.archief)
    .filter((p) => !zoek || `${p.naam} ${p.sleutel ?? ''} ${p.omschrijving}`.toLowerCase().includes(zoek.toLowerCase()))
    .sort((a, b) => a.volgorde - b.volgorde || (b.telling?.laatst ?? b.updatedAt) - (a.telling?.laatst ?? a.updatedAt)),
  [projecten, filter, zoek])

  return (
    <div className="pagina">
      <header className="pagina-kop">
        <div>
          <h1>Projecten</h1>
          <p className="stil">Elk project hier bestaat ook in Werk in het dashboard, en andersom.</p>
        </div>
        <Knop soort="hoofd" icoon={<Plus size={16} />} onClick={() => zetParam('nieuw', '1')}>Nieuw project</Knop>
      </header>

      <div className="werkbalk">
        <Tabs<Filter> waarde={filter} onKies={setFilter} tabs={[
          { key: 'lopend', label: 'Lopend' },
          { key: 'alle', label: 'Alle' },
          { key: 'archief', label: 'Archief' },
        ]} />
        <span className="vul" />
        <div className="zoekveld">
          <Search size={14} />
          <input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek een project…" />
        </div>
      </div>

      {!geladen ? <Laden /> : !zicht.length ? (
        <Leeg icoon={<FolderKanban size={28} />} titel={zoek ? 'Niets gevonden' : 'Nog geen projecten hier'}
          tekst={filter === 'archief' ? 'Er staat niets in het archief.' : 'Begin er een; de taken komen meteen ook in het dashboard.'}>
          {filter !== 'archief' && <Knop soort="hoofd" icoon={<Plus size={16} />} onClick={() => zetParam('nieuw', '1')}>Nieuw project</Knop>}
        </Leeg>
      ) : (
        <div className="project-tabel">
          <div className="project-tabel-kop">
            <span>Project</span><span>Status</span><span>Voortgang</span><span>Sprint</span><span>Eigenaar</span><span>Bijgewerkt</span>
          </div>
          {zicht.map((p) => <ProjectRegel key={p.id} p={p} eigenaar={mensen.find((m) => m.id === p.eigenaar)?.naam ?? p.eigenaarNaam} />)}
        </div>
      )}

      {nieuw && <NieuwProject onSluit={() => zetParam('nieuw', null)} />}
    </div>
  )
}

function ProjectRegel({ p, eigenaar }: { p: Project; eigenaar: string | null }) {
  const t = p.telling
  return (
    <Link naar={`/p/${p.id}/bord`} className="project-tabel-regel">
      <span className="project-naam">
        <span className={`project-sleutel t-${p.kleur}`}>{p.sleutel ?? '—'}</span>
        <span>
          <strong>{p.naam}</strong>
          {p.githubRepo && <small className="stil"><GitBranch size={11} /> {p.githubRepo.split('/')[1]}</small>}
        </span>
      </span>
      <span><Badge kleur={p.status === 'actief' ? 'ok' : p.status === 'gepauzeerd' ? 'warn' : p.status === 'idee' ? 'info' : 'paars'}>{PROJECT_STATUS[p.status]}</Badge></span>
      <span className="project-voortgang">
        <Voortgang klaar={t?.klaar ?? 0} totaal={t?.totaal ?? 0} kleur={p.kleur} />
        <small className="stil">{t?.klaar ?? 0}/{t?.totaal ?? 0}{t?.teLaat ? <span className="gevaar"> · {t.teLaat} te laat</span> : null}</small>
      </span>
      <span className="stil">{p.sprintActief ? p.sprintActief.naam : '—'}</span>
      <span className="eigenaar">{eigenaar ? <><Avatar naam={eigenaar} id={p.eigenaar} grootte={20} />{eigenaar}</> : '—'}</span>
      <span className="stil">{t?.laatst ? geleden(t.laatst) : datum(p.updatedAt)}</span>
    </Link>
  )
}

/* ------------------------------------------------------------------ *
 *  Een nieuw project
 * ------------------------------------------------------------------ */

export function NieuwProject({ onSluit }: { onSluit: () => void }) {
  const githubAan = useStaat((s) => s.github.ingesteld)
  const org = useStaat((s) => s.github.org)
  const ik = useStaat((s) => s.ik)!
  const { mensen } = useBasis()
  const [naam, setNaam] = useState('')
  const [sleutel, setSleutel] = useState('')
  const [sleutelZelf, setSleutelZelf] = useState(false)
  const [omschrijving, setOmschrijving] = useState('')
  const [kleur, setKleur] = useState<Kleur>('info')
  const [repo, setRepo] = useState('')
  const [repos, setRepos] = useState<Repo[]>([])
  const [eind, setEind] = useState('')
  const [team, setTeam] = useState<string[]>([ik.id])
  const [bezig, setBezig] = useState(false)

  useEffect(() => {
    if (githubAan) github.repos().then(setRepos).catch(() => setRepos([]))
  }, [githubAan])

  /* De sleutel volgt de naam, tot iemand hem zelf aanpast. */
  useEffect(() => {
    if (sleutelZelf) return
    const woorden = naam.toUpperCase().replace(/[^A-Z0-9 ]/g, '').split(/\s+/).filter(Boolean)
    const s = woorden.length > 1 ? woorden.map((w) => w[0]).join('').slice(0, 4) : (woorden[0] ?? '').slice(0, 3)
    setSleutel(s.replace(/^[0-9]+/, ''))
  }, [naam, sleutelZelf])

  async function maak() {
    if (!naam.trim() || bezig) return
    setBezig(true)
    try {
      const p = await api.projectOpslaan({
        naam: naam.trim(),
        sleutel: sleutel || null,
        omschrijving,
        kleur,
        githubRepo: repo ? `${org}/${repo}` : null,
        eindAt: vanInvoer(eind),
        startAt: Date.now(),
      })
      if (team.length > 1 || team[0] !== ik.id) {
        await api.ledenZetten(p.id, team.map((id) => ({ id, rol: id === ik.id ? 'eigenaar' : 'lid' })))
      }
      projectBijwerken(p)
      void laadProjecten()
      meld(`${p.naam} (${p.sleutel}) is aangemaakt`)
      onSluit()
      navigeer(`/p/${p.id}/bord`)
    } catch (e) { meldFout(e) }
    setBezig(false)
  }

  const teamKandidaten = mensen.filter((m) => m.rollen.includes('developer') || m.rollen.includes('management'))

  return (
    <Venster titel="Nieuw project" onSluit={onSluit} breed={640} bewustSluiten={!!naam}
      voet={<>
        <span className="vul" />
        <Knop soort="stil" onClick={onSluit}>Annuleren</Knop>
        <Knop soort="hoofd" bezig={bezig} disabled={!naam.trim()} onClick={() => void maak()}>Project aanmaken</Knop>
      </>}>
      <div className="formulier">
        <Veld label="Naam" breed>
          <input className="invoer groot" autoFocus value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Bijvoorbeeld: Nieuwe kassa" />
        </Veld>
        <Veld label="Sleutel" hulp={sleutel ? `Taken heten dan ${sleutel}-1, ${sleutel}-2, …` : 'Twee tot tien letters; daar volgen de taaknummers uit.'}>
          <input className="invoer mono" value={sleutel} maxLength={10}
            onChange={(e) => { setSleutelZelf(true); setSleutel(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')) }} />
        </Veld>
        <Veld label="Kleur">
          <div className="kleurkeuze">
            {KLEUREN.map((k) => (
              <button key={k} type="button" className={`kleur-bol t-${k} ${kleur === k ? 'actief' : ''}`} onClick={() => setKleur(k)} aria-label={k} />
            ))}
          </div>
        </Veld>
        <Veld label="Waar gaat het over?" breed>
          <textarea className="invoer" rows={3} value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)}
            placeholder="Het doel in een paar zinnen. De AI leest dit mee als je hem iets vraagt over dit project." />
        </Veld>
        {githubAan && (
          <Veld label="Repository" hulp="Mag leeg; kan later. Een nieuwe repo maak je bij Code.">
            <Kies<string> waarde={repo} onKies={setRepo}
              opties={[{ key: '', label: 'Geen' }, ...repos.map((r) => ({ key: r.naam, label: r.naam }))]} />
          </Veld>
        )}
        <Veld label="Streefdatum">
          <input type="date" className="invoer" value={eind} onChange={(e) => setEind(e.target.value)} />
        </Veld>
        <Veld label="Team" breed hulp="Wie erin zit, ziet het project bovenaan in het menu.">
          <div className="team-keuze">
            {teamKandidaten.map((m) => (
              <label key={m.id} className={`team-lid ${team.includes(m.id) ? 'actief' : ''}`}>
                <input type="checkbox" checked={team.includes(m.id)}
                  onChange={() => setTeam((t) => (t.includes(m.id) ? t.filter((x) => x !== m.id) : [...t, m.id]))} />
                <Avatar naam={m.naam} id={m.id} grootte={20} />
                {m.naam}
              </label>
            ))}
          </div>
        </Veld>
      </div>
    </Venster>
  )
}
